<template>
  <div class="tenant-selector">
    <div class="header">
      <h2>🏢 Select Company</h2>
      <p>Choose a company to generate AI-powered schedules for</p>
    </div>

    <!-- Search and filters -->
    <div class="search-section">
      <div class="search-input">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search companies..."
          @input="debouncedSearch"
          class="search-field"
        >
        <span class="search-icon">🔍</span>
      </div>
      
      <div class="filters">
        <select v-model="selectedStatus" @change="handleFilterChange" class="filter-select">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
        </select>
        
        <button @click="refreshTenants" :disabled="loading" class="refresh-btn">
          {{ loading ? '🔄' : '↻' }} Refresh
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading companies...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h4>Failed to load companies</h4>
      <p>{{ error }}</p>
      <button @click="refreshTenants" class="retry-btn">Try Again</button>
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredTenants.length === 0" class="empty-state">
      <div class="empty-icon">🏢</div>
      <h4>No companies found</h4>
      <p v-if="searchQuery">Try adjusting your search criteria</p>
      <p v-else>No companies are currently available for AI scheduling</p>
    </div>

    <!-- Tenant grid -->
    <div v-else class="tenant-grid">
      <div
        v-for="tenant in filteredTenants"
        :key="tenant.id"
        @click="selectTenant(tenant)"
        class="tenant-card"
        :class="{ 
          'selected': selectedTenant?.id === tenant.id,
          'trial': tenant.billing_status === 'trial'
        }"
      >
        <div class="tenant-header">
          <h3 class="tenant-name">{{ tenant.name }}</h3>
          <div class="tenant-status">
            <span 
              class="status-badge" 
              :class="tenant.billing_status"
            >
              {{ tenant.billing_status?.toUpperCase() }}
            </span>
          </div>
        </div>

        <div class="tenant-info">
          <div class="info-row">
            <span class="info-label">Schema:</span>
            <span class="info-value">{{ tenant.schema_name || tenant.subdomain || 'N/A' }}</span>
          </div>
          
          <div class="info-row" v-if="tenant.active_user_count !== undefined">
            <span class="info-label">Users:</span>
            <span class="info-value">{{ tenant.active_user_count }}</span>
          </div>
          
          <div class="info-row" v-if="tenant.created_at">
            <span class="info-label">Created:</span>
            <span class="info-value">{{ formatDate(tenant.created_at) }}</span>
          </div>

          <div class="info-row" v-if="tenant.next_billing_date">
            <span class="info-label">Next Billing:</span>
            <span class="info-value">{{ formatDate(tenant.next_billing_date) }}</span>
          </div>
        </div>

        <div class="tenant-actions">
          <button 
            @click.stop="generateSchedule(tenant)"
            class="generate-btn"
            :disabled="!tenant.allowed_for_ai"
          >
            🤖 Generate Schedule
          </button>
          
          <button 
            @click.stop="viewProposals(tenant)"
            class="proposals-btn"
          >
            📋 View Proposals
          </button>
        </div>

        <!-- Selected indicator -->
        <div v-if="selectedTenant?.id === tenant.id" class="selected-indicator">
          ✓ Selected
        </div>
      </div>
    </div>

    <!-- Pagination (if needed) -->
    <div v-if="totalTenants > filteredTenants.length" class="pagination">
      <button 
        @click="loadMore" 
        :disabled="loading"
        class="load-more-btn"
      >
        Load More Companies
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAISchedulerStore } from '../../stores/aiScheduler.js'

const emit = defineEmits(['tenant-selected', 'generate-schedule', 'view-proposals'])

const aiSchedulerStore = useAISchedulerStore()

// Reactive state
const searchQuery = ref('')
const selectedStatus = ref('')
const debouncedSearchTimer = ref(null)

// Computed properties
const loading = computed(() => aiSchedulerStore.tenantLoading)
const error = computed(() => aiSchedulerStore.tenantError)
const tenants = computed(() => aiSchedulerStore.tenants)
const selectedTenant = computed(() => aiSchedulerStore.selectedTenant)

const filteredTenants = computed(() => {
  let filtered = [...tenants.value]
  
  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(tenant =>
      tenant.name.toLowerCase().includes(query) ||
      tenant.schema_name?.toLowerCase().includes(query) ||
      tenant.subdomain?.toLowerCase().includes(query)
    )
  }
  
  // Apply status filter
  if (selectedStatus.value) {
    filtered = filtered.filter(tenant => tenant.billing_status === selectedStatus.value)
  }
  
  return filtered
})

const totalTenants = computed(() => tenants.value.length)

// Methods
const refreshTenants = async () => {
  try {
    await aiSchedulerStore.loadTenants({
      search: searchQuery.value || undefined,
      billing_status: selectedStatus.value || undefined
    })
  } catch (error) {
    console.error('Failed to refresh tenants:', error)
  }
}

const debouncedSearch = () => {
  if (debouncedSearchTimer.value) {
    clearTimeout(debouncedSearchTimer.value)
  }
  
  debouncedSearchTimer.value = setTimeout(() => {
    refreshTenants()
  }, 500)
}

const handleFilterChange = () => {
  refreshTenants()
}

const selectTenant = async (tenant) => {
  try {
    await aiSchedulerStore.selectTenant(tenant.id)
    emit('tenant-selected', tenant)
  } catch (error) {
    console.error('Failed to select tenant:', error)
  }
}

const generateSchedule = (tenant) => {
  emit('generate-schedule', tenant)
}

const viewProposals = (tenant) => {
  emit('view-proposals', tenant)
}

const loadMore = async () => {
  // Implement pagination if needed
  console.log('Load more tenants - pagination not implemented yet')
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

// Lifecycle
onMounted(async () => {
  // Initialize service if not already done
  if (!aiSchedulerStore.isServiceAuthenticated) {
    try {
      await aiSchedulerStore.initializeService()
    } catch (error) {
      console.error('Failed to initialize AI scheduler service:', error)
    }
  }
  
  // Load tenants
  await refreshTenants()
})

// Watch for errors and clear them after a delay
watch(() => error.value, (newError) => {
  if (newError) {
    setTimeout(() => {
      aiSchedulerStore.clearTenantError()
    }, 10000) // Clear error after 10 seconds
  }
})
</script>

<style scoped>
.tenant-selector {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
}

.header h2 {
  margin: 0 0 0.5rem 0;
  color: #1a1a1a;
  font-size: 2rem;
  font-weight: 600;
}

.header p {
  margin: 0;
  color: #6b7280;
  font-size: 1.1rem;
}

.search-section {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.search-input {
  position: relative;
  flex: 1;
  min-width: 300px;
}

.search-field {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.search-field:focus {
  outline: none;
  border-color: #3b82f6;
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 1.25rem;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.filter-select {
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  background: white;
}

.refresh-btn {
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: #e5e7eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state, .error-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon, .empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-state h4, .empty-state h4 {
  margin: 0 0 0.5rem 0;
  color: #1a1a1a;
  font-size: 1.25rem;
}

.error-state p, .empty-state p {
  margin: 0 0 1.5rem 0;
  color: #6b7280;
}

.retry-btn {
  padding: 0.75rem 1.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
}

.retry-btn:hover {
  background: #dc2626;
}

.tenant-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.tenant-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.tenant-card:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
}

.tenant-card.selected {
  border-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.tenant-card.trial {
  border-left: 4px solid #f59e0b;
}

.tenant-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.tenant-name {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
  flex: 1;
}

.tenant-status {
  margin-left: 1rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.trial {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.suspended {
  background: #fef2f2;
  color: #991b1b;
}

.tenant-info {
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.info-label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.info-value {
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 600;
}

.tenant-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
}

.generate-btn, .proposals-btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.generate-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.generate-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.proposals-btn {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.proposals-btn:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.selected-indicator {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #10b981;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.pagination {
  text-align: center;
  margin-top: 2rem;
}

.load-more-btn {
  padding: 1rem 2rem;
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s;
}

.load-more-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .tenant-selector {
    padding: 1rem;
  }
  
  .search-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    min-width: auto;
  }
  
  .tenant-grid {
    grid-template-columns: 1fr;
  }
  
  .tenant-actions {
    flex-direction: column;
  }
}
</style>